import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandKit, ContentPreferences } from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

export interface Variables {
  name?: string
  content_preferences?: ContentPreferences
}

interface Props {
  id: number
}

interface Data {
  brand_kit: BrandKit
}

interface Context {
  previousBrandKit?: Data
}

export const updateBrandKit = async ({
  id,
  workspaceId,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${id}`, {
    method: 'PUT',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update brand kit', {
      metaData: {
        id,
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useUpdateBrandKit = ({ id }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, Context>(
    ['workspaces', currentWorkspaceId, 'brandKits', 'detail', id],
    (variables) => {
      return updateBrandKit({ id, workspaceId: currentWorkspaceId, ...variables })
    },
    {
      onMutate: async (variables) => {
        // Use the exact same cache key structure as useBrandKit
        const cacheKey = [
          'workspaces',
          currentWorkspaceId,
          'brandKits',
          'detail',
          id,
          { includeAllMediaFiles: false, includeBrandVisualAnalysis: false },
        ]

        await queryClient.cancelQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', id])
        const previousBrandKit = queryClient.getQueryData(cacheKey) as Data | undefined

        queryClient.setQueryData(cacheKey, (old: any) => ({
          ...old,
          brand_kit: {
            ...old?.brand_kit,
            ...variables,
          },
        }))

        return { previousBrandKit }
      },
      onError: (_, __, context) => {
        const cacheKey = [
          'workspaces',
          currentWorkspaceId,
          'brandKits',
          'detail',
          id,
          { includeAllMediaFiles: false, includeBrandVisualAnalysis: false },
        ]
        queryClient.setQueryData(cacheKey, context?.previousBrandKit)
      },
      onSettled: () => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'list'])
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', id])
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandProfile'])
      },
    },
  )
}
